import axios from 'axios';
import { useAuthStore } from '../store/auth.store';
import { queryClient } from './queryClient';

export const api = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // Required: sends the httpOnly refresh-token cookie on every request
});

// Attach Bearer token from store on every request
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Deduplicates concurrent refresh calls: if multiple queries all get 401 at the same
// refetchInterval, only ONE refresh request is made. All concurrent retries share the
// same in-flight promise — the rotating refresh token is single-use so parallel calls
// would otherwise invalidate each other and trigger spurious logouts.
let refreshPromise: Promise<string> | null = null;

// Auto-refresh on 401: the refresh token lives in an httpOnly cookie — no explicit token needed
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    // Never attempt a token refresh for auth-specific endpoints — a 401 there means
    // the credentials themselves are wrong, not that the session expired.
    const isAuthEndpoint = original.url?.includes('/auth/');
    if (error.response?.status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post('/api/v1/auth/refresh', {}, { withCredentials: true })
            .then(({ data }) => {
              useAuthStore.getState().setToken(data.accessToken);
              return data.accessToken as string;
            })
            .finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch {
        queryClient.clear(); // evict stale data before forcing re-login
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);
